/**
 * 通知イベント → 遷移先 (deep link) の解決を一箇所に集約する resolver。
 *
 * 背景 (notification-event-deep-link-routing):
 * 旧実装は `NotificationDrawer` / `notificationPopup` の 2 箇所がそれぞれ
 * 「caseId があるか」だけを見て `/cases/{caseId}?tab=message` へ固定遷移しており、
 * `notification.type` を一切参照していなかった。そのため書類アップロード通知を
 * クリックしてもメッセージタブに着地し、案件に紐づかない通知 (CW / LINE /
 * 顧客ダイレクトメッセージ) はクリックしても何も起きなかった。
 *
 * 設計上の不変条件:
 * - **通知本文 (title / message / messagePreview) を解析しない。** 表示文言を変えても
 *   遷移先が変わらないことを保証する (日本語ラベル変更で routing が壊れない)。
 * - 解決に使ってよいのは `type` と、BE が既に返している安定 metadata
 *   (`caseId` / `collectionId` / `clientId`) だけ。
 * - 未知 type・legacy レコード・ID 欠落では **throw せず `null`** を返す。
 *   `null` は「遷移しない」を意味し、無関係な画面へ飛ばさない。
 * - 新しい `NotificationType` を追加すると `DESTINATION_RULES` の網羅性検査で
 *   コンパイルエラーになるため、destination 未定義のまま増えることがない。
 */

import type { Notification, NotificationType } from '@/types';

/**
 * 顧客詳細のタブ key (ClientDetailPage の `TabKey` と対応する)。
 *
 * deep-link で開けるのは `messages` だけに限定する
 * (notification-banner-readability-20260820)。URL から任意タブを開けるようにすると、
 * 権限で出し分けている受任前タブまで query で選択できてしまうため広げない。
 */
export type ClientTabKey = 'messages';

/** 案件詳細のタブ key (CaseDetailPage の `CaseTab` と対応する)。 */
export type CaseTabKey =
  | 'basic_info'
  | 'payments'
  | 'checklist'
  | 'calendar'
  | 'history'
  | 'files'
  | 'messages'
  | 'case_memo'
  | 'household_budget'
  | 'petition_prep';

/**
 * resolver の入力。legacy レコードや `Pick` 済みの部分オブジェクトでも扱えるよう
 * 意図的に緩い型にしている (型に無い値が来ても null に落として安全側へ倒す)。
 */
export interface NotificationDestinationInput {
  resourceId?: string | null;
  type?: string | null;
  caseId?: string | null;
  collectionId?: string | null;
  clientId?: string | null;
}

/** 遷移先の解決規則。 */
type DestinationRule =
  /**
   * 案件詳細の指定タブへ。caseId が無い場合、`collectionFallback` が true なら
   * `/collections/{collectionId}` へ退避する。
   */
  | { kind: 'caseTab'; tab: CaseTabKey; collectionFallback: boolean }
  /**
   * メッセージ対応 workspace (`/message-workspace`) へ deep-link する
   * (dashboard-message-reply-deeplink-20260826 /
   *  message-notification-link-routing-investigation-20260831)。
   *
   * caseId が必須で、`collectionId` があれば `&collectionId=` を付けてスレッドまで指定する
   * (受け側 `MessageWorkspacePage` が query を解釈し、実在すれば explicit 選択になる)。
   * caseId が無い場合の挙動は `caseTab` と同じ — `collectionFallback` が true なら
   * `/collections/{collectionId}` へ退避、それも無ければ解決不能 (null)。
   */
  | { kind: 'messageWorkspace'; collectionFallback: boolean }
  /** 固定パス (query 無しの neutral landing)。 */
  | { kind: 'fixed'; path: string }
  /**
   * 顧客詳細へ。clientId が無ければ解決不能。
   * `tab` を指定すると `?tab=` を付けて該当タブへ deep-link する
   * (受け側 `ClientDetailPage` が query を解釈する)。
   */
  | { kind: 'client'; tab?: ClientTabKey };

/**
 * notification type → 遷移先規則。BE `NotificationType` (18 値) と 1:1 で対応する。
 *
 * `collectionFallback` の使い分け:
 * - 資料回収 (collection) に紐づくイベントは、caseId が空でも `collectionId` が
 *   実在の collection を指すため `/collections/{id}` へ退避できる。
 * - `budget.url_shared` だけは `collectionId` に **budgetId** が入る (BE の
 *   `shareBudgetEmail` が `collectionId: budgetId` で書き込む) ため、退避すると
 *   存在しない collection を開いて 404 になる。よって fallback を無効にする。
 */
const DESTINATION_RULES: Record<NotificationType, DestinationRule> = {
  // 書類そのものの状態変化 → チェックリスト (書類回収) タブ。
  // 確認 / 差し戻し操作もこの面で行うため、書類を「見て次の操作をする」正本 surface。
  'item.uploaded': { kind: 'caseTab', tab: 'checklist', collectionFallback: true },
  'item.resubmitted': { kind: 'caseTab', tab: 'checklist', collectionFallback: true },
  'item.confirmed': { kind: 'caseTab', tab: 'checklist', collectionFallback: true },
  'item.rejected': { kind: 'caseTab', tab: 'checklist', collectionFallback: true },
  'collection.requester_item_added': {
    kind: 'caseTab',
    tab: 'checklist',
    collectionFallback: true,
  },
  // steering 630: 依頼者の「後日提出」申告 / 「期限の延長のお願い」→ チェックリストで確認する
  'collection.requester_item_deferred': {
    kind: 'caseTab',
    tab: 'checklist',
    collectionFallback: true,
  },
  'collection.requester_extension_requested': {
    kind: 'caseTab',
    tab: 'checklist',
    collectionFallback: true,
  },
  'collection.completed': { kind: 'caseTab', tab: 'checklist', collectionFallback: true },

  // 受任→案件自動立ち上げ: 受任で新規作成された案件そのものへ着地させる。
  // 通知の目的が「担当事務員の変更と案件編集」なので、書類回収ではなく案件の概要面が正しい。
  // collectionId は自動展開されたコレクションを指すが、依頼される操作は案件側にあるため
  // collection への退避はしない。
  'case.accepted': { kind: 'caseTab', tab: 'basic_info', collectionFallback: false },

  // 依頼者とのポータルメッセージは送受信とも メッセージ対応 workspace へ直行する
  // (受信: dashboard-message-reply-deeplink-20260826 /
  //  送信: message-notification-link-routing-investigation-20260831)。
  //
  // 受信は「読んで返信する」ので返信 composer のある面が着地先。送信控えも同じ面に揃える理由は
  // 導線統一だけではない — **案件詳細のメッセージタブは通知が持つ collectionId を受け取らず、
  // 自前で現役世代 1 件 (ADR-041 の `selectPrimaryCollection`) を選び、スレッド切替 UI も持たない**。
  // そのため送信可能な資料回収が複数ある案件では「通知が指すスレッドと別のスレッドが開く」。
  // workspace は `collectionId` を explicit 選択として尊重するので、寄せることで誤スレッド表示も消える。
  //
  // 送受信とも通知は caseId + collectionId を両方持つため、スレッドまで選択済みで開ける
  // (collectionId 欠落時の退避は両者で同一 = `/collections/{id}`)。
  'portal.message_received': { kind: 'messageWorkspace', collectionFallback: true },
  'portal.message_sent': { kind: 'messageWorkspace', collectionFallback: true },

  // 事務所から依頼者への送信記録 (リマインド / URL 共有 / パスワード通知) → 対応履歴タブ。
  'collection.reminded': { kind: 'caseTab', tab: 'history', collectionFallback: true },
  'collection.url_shared': { kind: 'caseTab', tab: 'history', collectionFallback: true },
  'collection.password_notified': { kind: 'caseTab', tab: 'history', collectionFallback: true },
  // resend-bounce-webhook: 依頼者宛メールの不達 / 迷惑メール申告 → 対応履歴タブ (監査 email.* が並ぶ)。回収が無ければ依頼者へ退避
  'email.bounced': { kind: 'caseTab', tab: 'history', collectionFallback: true },
  'email.complained': { kind: 'caseTab', tab: 'history', collectionFallback: true },

  // 家計表の入力依頼 → 家計表タブ (対象物へ直行する)。
  // collectionId は budgetId のため collection への退避はしない (上記コメント参照)。
  'budget.url_shared': { kind: 'caseTab', tab: 'household_budget', collectionFallback: false },

  // 外部チャネル。room / friend 単位の deep link は受け側 (ChatworkPage / LinePage) が
  // query を解釈しないため、neutral な一覧ページへ着地させる。
  // steering 577: タスクの遷移先は一覧画面 (SPA route は `/client-tasks` のまま)。
  // 個別 task の deep link は持たない — 一覧が既定 scope 「自分の担当」で開くので、
  // 割り当てられた本人はそこで対象を見つけられる。
  'task.follow_up': { kind: 'fixed', path: '/client-tasks' },
  'case.patrol_daily': { kind: 'fixed', path: '/case-patrol' },
  'task.assigned': { kind: 'fixed', path: '/client-tasks' },
  'task.reassigned': { kind: 'fixed', path: '/client-tasks' },
  'cw.message_received': { kind: 'fixed', path: '/chatwork' },
  'cw.message_sent': { kind: 'fixed', path: '/chatwork' },
  'line.message_received': { kind: 'fixed', path: '/line' },
  'line.message_sent': { kind: 'fixed', path: '/line' },

  // 顧客ダイレクトメッセージ (案件・資料回収に紐づかない) → 顧客詳細のメッセージタブ。
  // 旧実装は `/clients/{id}` へ着地させていたが、ClientDetailPage の既定タブは属性のため
  // 「メッセージを確認」導線を辿ってもメッセージ面に着かなかった
  // (notification-banner-readability-20260820)。
  'client.message_received': { kind: 'client', tab: 'messages' },

  // initiative email-undeliverable-visibility: 依頼者に「メール不達」の印が付いた。直す場所 (メールアドレスの確認 /
  // 「メール送信を再開」) は依頼者詳細の属性タブにあるので、案件ではなく依頼者へ着地させる (既定タブ = 属性)。
  'client.email_undeliverable': { kind: 'client' },
  // steering 649: 外部サービスのフォーム回答。中身は依頼者の対話記録に入っている。
  // 対話記録タブは受任前情報の権限で出し分けているため **deep-link では開かない**
  // (ClientTabKey を広げない方針。上の注記参照)。依頼者詳細の既定タブへ着地させる。
  'inbound.form_response': { kind: 'client' },
};

/** 空文字・空白のみ・null/undefined を等しく「値なし」として扱う。 */
function nonEmpty(value: string | null | undefined): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * 通知から遷移先パスを解決する。
 *
 * @returns 遷移先パス。解決できない場合 (未知 type / 必要な ID が欠落) は `null`。
 *          `null` のときは遷移させないこと (代替画面へ飛ばさない)。
 */
export function resolveNotificationDestination(
  notification: NotificationDestinationInput | null | undefined,
): string | null {
  if (!notification) return null;

  const type = nonEmpty(notification.type);
  if (!type) return null;

  // legacy / 未知 type は規則が無いので解決不能。throw はしない。
  const rule = (DESTINATION_RULES as Record<string, DestinationRule | undefined>)[type];
  if (!rule) return null;

  if (type === 'task.follow_up' && nonEmpty(notification.resourceId))
    return `/client-tasks?taskId=${encodeURIComponent(notification.resourceId!)}`;
  if (rule.kind === 'fixed') return rule.path;

  if (rule.kind === 'messageWorkspace') {
    const caseId = nonEmpty(notification.caseId);
    const collectionId = nonEmpty(notification.collectionId);
    if (caseId) {
      const base = `/message-workspace?caseId=${encodeURIComponent(caseId)}`;
      // collectionId は付加情報。無くても workspace 側がスレッドを解決するため遷移は成立する。
      return collectionId ? `${base}&collectionId=${encodeURIComponent(collectionId)}` : base;
    }
    // caseId 欠落時は workspace がスレッド候補を引けない (GET /collections?caseId= が張れない)
    // ため、従来どおり collection 単体の面へ退避する。
    if (rule.collectionFallback && collectionId) {
      return `/collections/${encodeURIComponent(collectionId)}`;
    }
    return null;
  }

  if (rule.kind === 'client') {
    const clientId = nonEmpty(notification.clientId);
    // clientId 欠落は従来どおり解決不能 (null = 遷移しない)。tab は付加情報にすぎず、
    // 「tab があるから遷移できる」ことにはしない。
    if (!clientId) return null;
    const base = `/clients/${encodeURIComponent(clientId)}`;
    return rule.tab ? `${base}?tab=${rule.tab}` : base;
  }

  const caseId = nonEmpty(notification.caseId);
  // 提出通知は、既存の項目展開・スクロール導線に接続する。旧通知は従来の着地を保つ。
  const submittedItemId =
    type === 'item.uploaded' || type === 'item.resubmitted'
      ? nonEmpty(notification.resourceId)
      : null;
  if (submittedItemId) {
    const query = new URLSearchParams({ itemId: submittedItemId });
    if (caseId) return `/cases/${encodeURIComponent(caseId)}?tab=checklist&${query}`;
    const collectionId = nonEmpty(notification.collectionId);
    if (collectionId) return `/collections/${encodeURIComponent(collectionId)}?${query}`;
  }
  if (caseId) return `/cases/${encodeURIComponent(caseId)}?tab=${rule.tab}`;

  if (rule.collectionFallback) {
    const collectionId = nonEmpty(notification.collectionId);
    if (collectionId) return `/collections/${encodeURIComponent(collectionId)}`;
  }

  return null;
}

/**
 * `Notification` から直接解決するショートハンド。
 * drawer / popup の双方がこの 1 経路だけを使い、独自の分岐を持たないこと。
 */
export function notificationDestination(
  notification: Pick<Notification, 'type'> &
    Partial<Pick<Notification, 'caseId' | 'collectionId' | 'clientId' | 'resourceId'>>,
): string | null {
  return resolveNotificationDestination(notification);
}

/** テスト用: 規則を持つ type の一覧 (BE enum との parity 検証に使う)。 */
export const NOTIFICATION_TYPES_WITH_DESTINATION = Object.keys(
  DESTINATION_RULES,
) as NotificationType[];
